import { formatBN } from "@gearbox-protocol/sdk/onchain";
import { api } from "api";
import { isFinite as isFiniteN } from "lodash-es";
import { useMemo, useState } from "react";
import useAsyncFn from "react-use/lib/useAsyncFn";
import useAsyncRetry from "react-use/lib/useAsyncRetry";
import { createPublicClient, http } from "viem";
import { useAccount } from "wagmi";

import { useUnpausedForkStatus } from "../context";

export interface EthFaucetState {
  balance: string;
  loading: boolean;
  requesting: boolean;
  requestEth: (amount?: string) => void;
  refresh: () => void;
}

export default function useEthFaucet(): EthFaucetState {
  const { executionId, rpcUrl } = useUnpausedForkStatus();
  const { address: account } = useAccount();
  const [balance, setBalance] = useState("");

  const publicClient = useMemo(
    () => createPublicClient({ transport: http(rpcUrl) }),
    [rpcUrl],
  );

  const updateBalance = useAsyncRetry(async () => {
    if (account) {
      const bal = await publicClient.getBalance({ address: account });
      setBalance(`${formatBN(bal, 18)}`);
    } else {
      setBalance("-");
    }
  }, [account, publicClient, setBalance]);

  const [requestState, requestEth] = useAsyncFn(
    async (amountStr?: string) => {
      if (!account) {
        return;
      }
      const amount = parseInt(amountStr ?? "", 10);
      const hash = await api.requestEth(
        executionId,
        account,
        isFiniteN(amount) && amount > 0 ? amount : undefined,
      );
      await publicClient.waitForTransactionReceipt({ hash });
      updateBalance.retry();
    },
    [account, executionId, publicClient, updateBalance.retry],
  );

  return {
    balance,
    loading: updateBalance.loading,
    requesting: requestState.loading,
    requestEth,
    refresh: updateBalance.retry,
  };
}
